import React, { useMemo, useState } from 'react';
import { Card, Switch } from 'tea-component';
import classnames from 'classnames';
import Chart from '@/pages/dashboard/components/Chart';
import { useIntl } from 'umi';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { dynamicString } from '@home/utils/dynamicString';
import {
  resolveStudioKpiTitleIconSrc,
  resolveGameCoverFromGameList,
} from '@home/pages/studio/home/_constant';
import {
  formatTechlandAsOfDisplay,
  getTechlandFilterEndForSubtitle,
  getTechlandTrendTitleDateStr,
} from '@home/pages/studio/home/_trendUtils';
import { resolveTechlandShowTimeMetricKey } from '@home/pages/studio/home/_utils';
import Abnormal from '@home/components/Abnormal';
import {
  buildMultiMetricTrendChartOptions,
  DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_CN,
  DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_EN,
} from './chartOptions';
import type {
  IMetricSeriesConfig,
  ITechlandMultiMetricTrendCardProps,
  TTechlandDateTypeKey,
} from './types';
import './index.scss';

const TechlandMultiMetricTrendCard: React.FC<ITechlandMultiMetricTrendCardProps> = props => {
  const {
    className,
    style,
    data,
    dataKey,
    componentMetrics = [],
    filterVal,
    dateFormat = 'YYYY-MM-DD',
    xDateFormat = 'MM-DD',
    tooltipDateFormat,
    title,
    titleCn,
    titleByDateType,
    titleIconSrc,
    titleIconSrcKey,
    titleIconGameCode,
    subTitle,
    subTitleCn,
    subTitleDateFormat,
    subTitleDateFormatCn,
    showTimeMap,
    showTimeKeyByScope,
    showTimeKey,
    showTimeLoading,
    yAxisFormatter = 'intBmk',
    showLabel = false,
    targetLines: propTargetLines,
    targetLinesLoading = false,
    targetLinesToggleable = false,
    targetLinesToggleLabel = 'Yearly KPI',
    targetLinesToggleLabelCn,
    planValueForPercent,
    planProgressVsYearlyLabelEn,
    planProgressVsYearlyLabelCn,
    planProgressForMetricKeys,
    planProgressLabelTextStyle,
    seriesConfig,
    sevenTick2026Mode,
  } = props;

  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const slot = data?.[dataKey] as
    | {
      data?: { list?: unknown[]; };
      loading?: boolean;
      errorMsg?: string;
    }
    | undefined;
  const metricCardData = slot?.data;
  const loading = Boolean(slot?.loading);
  const errorMsg = slot?.errorMsg;
  const combinedLoading = loading || targetLinesLoading;

  const dateTypeStr =
    (filterVal as { date_type?: string; })?.date_type ?? 'daily';
  const titleDateTypeKey = (dateTypeStr === 'weekly' || dateTypeStr === 'monthly'
    ? dateTypeStr
    : 'daily') as TTechlandDateTypeKey;
  const rawDt = (filterVal as { date_type?: string; })?.date_type;
  let titleDateType: 'weekly' | 'monthly' | undefined;
  if (rawDt === 'weekly') {
    titleDateType = 'weekly';
  } else if (rawDt === 'monthly') {
    titleDateType = 'monthly';
  } else {
    titleDateType = undefined;
  }
  const dateStrToken = getTechlandTrendTitleDateStr(titleDateType);

  const titleCtx = useMemo(() => ({ dateStr: dateStrToken }), [dateStrToken]);

  const finalTitle = useMemo(() => {
    const mapped = titleByDateType?.[titleDateTypeKey];
    if (mapped?.en) {
      return getLanguage(isEn, mapped.en, mapped.cn ?? mapped.en);
    }
    const tEn = title?.includes('{{')
      ? dynamicString(title, titleCtx, title)
      : title;
    const tCn = titleCn?.includes('{{')
      ? dynamicString(titleCn, titleCtx, titleCn)
      : titleCn;
    return getLanguage(isEn, tEn, tCn ?? title);
  }, [isEn, title, titleCn, titleByDateType, titleDateTypeKey, titleCtx]);

  const filterEnd = getTechlandFilterEndForSubtitle(filterVal as Record<string, any>);
  const scopeForShowTimeKey = (filterVal as { scope?: string; } | undefined)
    ?.scope;
  const keyFromScopeMap =
    scopeForShowTimeKey && showTimeKeyByScope?.[scopeForShowTimeKey];
  const effectiveShowTimeKey =
    keyFromScopeMap ??
    showTimeKey ??
    resolveTechlandShowTimeMetricKey(scopeForShowTimeKey);
  const showTimeVal =
    effectiveShowTimeKey && showTimeMap?.[effectiveShowTimeKey]
      ? String(showTimeMap[effectiveShowTimeKey]).trim().slice(0, 10)
      : '';
  const asOfSource = showTimeVal || filterEnd;

  const subTitleResolved = useMemo(() => {
    if (
      showTimeLoading &&
      !(effectiveShowTimeKey && showTimeMap?.[effectiveShowTimeKey])
    ) {
      return '';
    }
    if (!subTitle && !subTitleCn) {
      return '';
    }
    const ctxEn = {
      dateStr: formatTechlandAsOfDisplay(asOfSource, true, subTitleDateFormat),
      _cardItemTime: asOfSource,
    };
    const ctxCn = {
      dateStr: formatTechlandAsOfDisplay(
        asOfSource,
        false,
        subTitleDateFormatCn ?? subTitleDateFormat,
      ),
      _cardItemTime: asOfSource,
    };
    const sEn =
      subTitle?.includes('{{')
        ? dynamicString(subTitle, ctxEn, subTitle)
        : subTitle;
    const sCn =
      subTitleCn?.includes('{{')
        ? dynamicString(subTitleCn, ctxCn, subTitleCn)
        : subTitleCn;
    return getLanguage(isEn, sEn, sCn ?? subTitle);
  }, [
    isEn,
    subTitle,
    subTitleCn,
    subTitleDateFormat,
    subTitleDateFormatCn,
    asOfSource,
    effectiveShowTimeKey,
    showTimeMap,
    showTimeLoading,
  ]);

  const studioData = data?.studioData as
    | { game_list?: Array<{ game_code?: string; cover?: string; }>; }
    | undefined;
  const gameCoverIcon = useMemo(
    () => resolveGameCoverFromGameList(studioData, titleIconGameCode),
    [studioData, titleIconGameCode],
  );
  const titleIconResolved = resolveStudioKpiTitleIconSrc({
    titleIconSrc: gameCoverIcon || titleIconSrc,
    titleIconSrcKey,
  });

  const list = (metricCardData as { list?: unknown[]; })?.list ?? [];
  const isNoData =
    !combinedLoading && !errorMsg && (!Array.isArray(list) || list.length === 0);

  const metrics: IMetricSeriesConfig[] = useMemo(() => {
    const fromComponent = (m: (typeof componentMetrics)[0]): IMetricSeriesConfig => ({
      metricKey: m.metricKey,
      enName: m.enName,
      formatter: m.formatter,
      showLabel,
    });
    if (seriesConfig?.length) {
      return seriesConfig.map(sc => {
        const cm = componentMetrics.find(c => c.metricKey === sc.metricKey);
        if (!cm) {
          return sc;
        }
        return {
          ...sc,
          formatter: sc.formatter ?? cm.formatter,
        };
      });
    }
    return componentMetrics.map(fromComponent);
  }, [seriesConfig, componentMetrics, showLabel]);

  const [targetLinesVisible, setTargetLinesVisible] = useState(true);

  const effectiveTargetLines =
    targetLinesToggleable && !targetLinesVisible ? undefined : propTargetLines;

  const planProgressLabelTemplate = useMemo(
    () =>
      getLanguage(
        isEn,
        planProgressVsYearlyLabelEn ?? DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_EN,
        planProgressVsYearlyLabelCn ?? DEFAULT_PLAN_PROGRESS_LABEL_TEMPLATE_CN,
      ),
    [isEn, planProgressVsYearlyLabelEn, planProgressVsYearlyLabelCn],
  );

  const chartOption = useMemo(
    () =>
      buildMultiMetricTrendChartOptions(
        list.map((item: any) => ({
          date: item?.date,
          metrics: item?.metrics,
        })),
        {
          metrics,
          yAxisFormatter,
          xDateFormat,
          tooltipDateFormat,
          dateType: dateTypeStr,
          dateFormat,
          planValueForPercent,
          labelTextStyle: planProgressLabelTextStyle,
          targetLines: effectiveTargetLines,
          planProgressLabelTemplate,
          planProgressForMetricKeys,
          localeIsEn: isEn,
          sevenTickAxis:
            sevenTick2026Mode && filterVal
              ? {
                  filterEndYmd: getTechlandFilterEndForSubtitle(
                    filterVal as Record<string, unknown>,
                  ),
                  period: dateTypeStr,
                }
              : undefined,
        },
      ),
    [
      list,
      metrics,
      yAxisFormatter,
      xDateFormat,
      tooltipDateFormat,
      dateTypeStr,
      dateFormat,
      planValueForPercent,
      planProgressLabelTextStyle,
      effectiveTargetLines,
      planProgressLabelTemplate,
      planProgressForMetricKeys,
      isEn,
      sevenTick2026Mode,
      filterVal,
    ],
  );

  const titleRight =
    targetLinesToggleable && propTargetLines?.length ? (
      <div className="dbh-techland-trend-card__toolbar">
        <span className="dbh-techland-trend-card__kpi-toggle">
          <span className="dbh-techland-trend-card__kpi-toggle-label">
            {getLanguage(isEn, targetLinesToggleLabel, targetLinesToggleLabelCn)}
          </span>
          <Switch
            value={targetLinesVisible}
            onChange={v => setTargetLinesVisible(Boolean(v))}
          />
        </span>
      </div>
    ) : null;

  const titleNode = (
    <div className="dbh-techland-trend-card__title-row">
      {titleIconResolved ? (
        <img
          className="dbh-techland-trend-card__title-icon"
          src={titleIconResolved}
          alt=""
        />
      ) : null}
      <div className="dbh-techland-trend-card__title-text">
        <span className="dbh-techland-trend-card__title-main">{finalTitle}</span>
        {subTitleResolved ? (
          <div className="dbh-techland-trend-card__subtitle">{subTitleResolved}</div>
        ) : null}
      </div>
    </div>
  );

  return (
    <Card
      className={classnames('dbh-techland-multi-metric-trend-card', className)}
      style={style}
    >
      <Card.Body title={titleNode} subtitle={null} operation={titleRight}>
        <Abnormal
          isLoading={combinedLoading}
          isError={Boolean(errorMsg)}
          isNoData={isNoData}
          style={{ height: 326 }}
          classNames="dbh-techland-trend-card__abnormal"
          component={
            <div className="dbh-techland-trend-card__chart-wrap">
              <Chart option={chartOption} notMerge />
            </div>
          }
        />
      </Card.Body>
    </Card>
  );
};

export default TechlandMultiMetricTrendCard;
